import { useRef, useState, useCallback } from "react";
import { useWavesurfer } from "@wavesurfer/react";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import { cn } from "@util/tailwind";

interface WaveformPlayerProps {
    src: string;
    duration?: number;
}

function formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
}

export function WaveformPlayer({ src, duration: propDuration }: WaveformPlayerProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [muted, setMuted] = useState(false);

    const { wavesurfer, isPlaying, currentTime } = useWavesurfer({
        container: containerRef,
        url: src,
        waveColor: "#d1d5db",
        progressColor: "#2563eb",
        cursorColor: "#2563eb",
        cursorWidth: 1,
        barWidth: 3,
        barGap: 2,
        barRadius: 2,
        height: 48,
        normalize: true,
    });

    const duration = wavesurfer?.getDuration() || propDuration || 0;

    const handlePlayPause = useCallback(() => {
        wavesurfer?.playPause();
    }, [wavesurfer]);

    const handleMute = useCallback(() => {
        const next = !muted;
        setMuted(next);
        wavesurfer?.setMuted(next);
    }, [wavesurfer, muted]);

    return (
        <div className="flex items-center gap-3">
            {/* Play/Pause */}
            <button
                onClick={handlePlayPause}
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-blue-600 hover:bg-blue-700 transition-colors"
            >
                {isPlaying ? (
                    <Pause className="w-4 h-4 text-white" />
                ) : (
                    <Play className="w-4 h-4 text-white ml-0.5" />
                )}
            </button>

            {/* Time */}
            <span className="text-xs text-gray-500 tabular-nums w-10 text-right flex-shrink-0">
                {formatTime(currentTime)}
            </span>

            {/* Waveform */}
            <div ref={containerRef} className="flex-1 cursor-pointer" />

            {/* Duration */}
            <span className="text-xs text-gray-400 tabular-nums w-10 flex-shrink-0">
                {duration > 0 ? formatTime(duration) : "--:--"}
            </span>

            {/* Mute */}
            <button
                onClick={handleMute}
                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
            >
                {muted ? (
                    <VolumeX className="w-4 h-4" />
                ) : (
                    <Volume2 className="w-4 h-4" />
                )}
            </button>
        </div>
    );
}
